import { clubTimeZone, getClubPublicConfig } from '../services/clubPublicConfig';

/**
 * Formatage club (montants, dates, heures) : SEULE source de la locale, de la devise et du pays
 * (ERP → Configuration → Région : `region.locale`, `region.currency`, `region.country`, `region.timezone`,
 * lus dans la config publique). Plus de 'fr-FR', '€' ou 'EUR' codés en dur dans les pages. Les libellés restent
 * en français : seuls les formats suivent la config.
 *
 * Fonctions (pas de constantes) : la config publique arrive après le chargement des modules.
 */

const DEFAULT_LOCALE = 'fr-BE';

export { clubTimeZone };

/** Locale configurée du club (BCP 47), ex. 'fr-BE'. */
export function clubLocale() {
  const raw = String(getClubPublicConfig()['region.locale'] || '').trim().replace('_', '-');
  return raw || DEFAULT_LOCALE;
}

/**
 * Locale d'affichage des dates et nombres. L'ERP a été écrit en 'fr-FR' alors que le club est en 'fr-BE' :
 * mêmes noms de jours / mois, mais dates numériques différentes (fr-BE « 3/10/26 », fr-FR « 03/10/2026 »).
 * Pour garder le rendu d'origine, fr-BE s'affiche en fr-FR ; toute autre locale est utilisée telle quelle.
 * Les écrans qui utilisaient déjà 'fr-BE' passent par `clubLocale()`.
 */
export function displayLocale() {
  const locale = clubLocale();
  return locale.toLowerCase() === 'fr-be' ? 'fr-FR' : locale;
}

/** Devise ISO 4217 en MAJUSCULES (Intl), ex. 'EUR'. */
export function clubCurrency() {
  const c = String(getClubPublicConfig()['region.currency'] || '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(c) ? c : 'EUR';
}

/** Pays ISO 3166 en MAJUSCULES, ex. 'BE'. */
export function clubCountry() {
  const c = String(getClubPublicConfig()['region.country'] || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(c) ? c : 'BE';
}

const symbolCache = new Map();

/** Symbole de la devise du club dans la locale d'affichage, ex. '€' (champs « Prix (€) », adornments). */
export function currencySymbol() {
  const locale = displayLocale();
  const currency = clubCurrency();
  const key = `${locale}|${currency}`;
  if (symbolCache.has(key)) return symbolCache.get(key);
  let symbol = currency === 'EUR' ? '€' : currency;
  try {
    const part = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .formatToParts(1)
      .find((p) => p.type === 'currency');
    if (part?.value) symbol = part.value;
  } catch {
    /* Intl incomplet : repli ci-dessus */
  }
  symbolCache.set(key, symbol);
  return symbol;
}

/** Centimes → « 12,50 € » (Intl, style monétaire de la locale d'affichage) ; '—' si absent/invalide. */
export function formatMoney(cents) {
  if (cents == null || !Number.isFinite(Number(cents))) return '—';
  return (Number(cents) / 100).toLocaleString(displayLocale(), { style: 'currency', currency: clubCurrency() });
}

/** Nombre dans la locale d'affichage (stats, pourcentages). */
export function formatNumber(n, options) {
  return Number(n).toLocaleString(displayLocale(), options);
}

/** Date (fuseau du navigateur, sauf `timeZone` passé ; voir `formatClubDateTime`). */
export function formatDate(d, options) {
  return d.toLocaleDateString(displayLocale(), options);
}

export function formatTime(d, options = { hour: '2-digit', minute: '2-digit' }) {
  return d.toLocaleTimeString(displayLocale(), options);
}

export function formatDateTime(d, options) {
  return d.toLocaleString(displayLocale(), options);
}

/** Date + heure dans le fuseau du CLUB, quel que soit celui du navigateur. */
export function formatClubDateTime(d, options) {
  return d.toLocaleString(displayLocale(), { ...options, timeZone: clubTimeZone() });
}
